import { useState, useEffect } from 'react'
import { ArrowLeft, ChevronDown } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { getMyOpenTrades, getMyClosedTrades } from '../../services/tradeService'
import { getTransactions, checkWithdrawalStatus } from '../../services/walletService'
import { getMyOrders } from '../../services/p2pService'
import { useAuth } from '../../context/AuthContext'
import Layout from '../../components/Layout/Layout'
import { formatAmount } from '../../lib/utils'
import '../../styles/history.css'

interface Trade {
    id: string
    pair: string
    type: 'Long' | 'Short'
    entryPrice: string
    pnl: string
    pnlPercent: string
    isProfit: boolean
    tradeType: string
    asset: string
    createdAt: string
    displayType?: string
    sentFrom?: string
    destination?: string
    amountText?: string
    // Futures-specific fields for the rich card layout (matches reference design)
    leverage?: number
    isOpen?: boolean
    stopProfitText?: string
    stopLossText?: string
    openPriceText?: string
    closePriceText?: string
    marginText?: string
    statusNote?: string
    timestampText?: string
}

function mapTrade(t: any): Trade {
    const pnl = t.status === 'open'
        ? (t.unrealizedPnL ?? 0)
        : (t.pnl ?? 0)
    const rawPct = t.status === 'open' && t.pnlPercentage != null
        ? t.pnlPercentage
        : (t.marginUsed > 0 ? (pnl / t.marginUsed) * 100 : 0)
    const asset = t.asset || t.symbol || 'Unknown'
    const typeLabel = (t.side === 'buy' || t.side === 'long') ? 'Long' : 'Short'
    const tradeMode = t.type === 'futures' ? 'Futures Trade' : 'Spot Trade'

    // Derive a human status/trigger line from the data we have, matching the
    // reference design's "Trigger TP closing" / "Trigger SL closing" / "Autonomous liquidation" labels.
    let statusNote = ''
    const closePriceNum = Number(t.closePrice || 0)
    const tpNum = Number(t.takeProfit || 0)
    const slNum = Number(t.stopLoss || 0)
    if (t.status === 'liquidated') {
        statusNote = t.liquidatedBy === 'admin' ? 'Liquidated by admin' : 'Autonomous liquidation'
    } else if (t.status === 'closed' && closePriceNum > 0) {
        if (tpNum > 0 && Math.abs(closePriceNum - tpNum) / tpNum < 0.005) statusNote = 'Trigger TP closing'
        else if (slNum > 0 && Math.abs(closePriceNum - slNum) / slNum < 0.005) statusNote = 'Trigger SL closing'
        else statusNote = 'Manually closed'
    } else if (t.status === 'open' || t.status === 'pending') {
        statusNote = 'Position open'
    }

    const ts = t.closedAt || t.createdAt || new Date().toISOString()

    return {
        id: t._id,
        pair: `${asset.split('/')[0]}/USDT`,
        type: typeLabel,
        entryPrice: `$${Number(t.entryPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
        pnl: `${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)}`,
        pnlPercent: `${rawPct >= 0 ? '+' : ''}${rawPct.toFixed(2)}%`,
        isProfit: pnl >= 0,
        tradeType: t.type || 'spot',
        asset,
        createdAt: t.createdAt || new Date().toISOString(),
        displayType: `${tradeMode} (${typeLabel})`,
        sentFrom: t.type === 'futures' ? 'Bicoin Futures Wallet' : 'Bicoin Spot Wallet',
        destination: 'Bicoin Spot Account',
        amountText: `${formatAmount(t.amount)} ${asset.split('/')[0]}`,
        leverage: Number(t.leverage || 1),
        isOpen: t.status === 'open' || t.status === 'pending',
        stopProfitText: tpNum > 0 ? tpNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—',
        stopLossText: slNum > 0 ? slNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—',
        openPriceText: Number(t.entryPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        closePriceText: closePriceNum > 0 ? closePriceNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—',
        marginText: Number(t.marginUsed || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        statusNote,
        timestampText: new Date(ts).toLocaleString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    }
}

// ─────────────────────────────────────────────────────────────
// Loading skeletons — shown while fetchTrades() is in flight
// ─────────────────────────────────────────────────────────────
function HistoryMobileSkeleton({ rows = 4 }: { rows?: number }) {
    return (
        <div className="history-trades-list" style={{ padding: '16px' }}>
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} className="history-skel-card">
                    <div className="history-skel-col">
                        <div className="history-skel" style={{ width: 70, height: 14 }} />
                        <div className="history-skel" style={{ width: 90, height: 11 }} />
                    </div>
                    <div className="history-skel-col right">
                        <div className="history-skel" style={{ width: 60, height: 14 }} />
                        <div className="history-skel" style={{ width: 50, height: 11 }} />
                    </div>
                </div>
            ))}
        </div>
    )
}

function HistoryDesktopSkeleton({ cols, rows = 5 }: { cols: number; rows?: number }) {
    return (
        <>
            {Array.from({ length: rows }).map((_, r) => (
                <tr key={r} className="history-skel-row">
                    {Array.from({ length: cols }).map((_, c) => (
                        <td key={c}>
                            <div className="history-skel" style={{ width: c === 0 ? 80 : 60, height: 13 }} />
                        </td>
                    ))}
                </tr>
            ))}
        </>
    )
}

export default function History() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const [activeHistoryTab, setActiveHistoryTab] = useState<'trade' | 'p2p' | 'transfer' | 'deposit' | 'withdrawal'>('trade')
    const [mainTab, setMainTab] = useState<'spot' | 'futures'>('spot')
    const [currencyFilter, setCurrencyFilter] = useState('All Coins')
    const [typeFilter, setTypeFilter] = useState('All Types')
    const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false)
    const [showTypeDropdown, setShowTypeDropdown] = useState(false)
    const [historyTrades, setHistoryTrades] = useState<Trade[]>([])
    const [openSpotTrades, setOpenSpotTrades] = useState<Trade[]>([])
    const [dbTransactions, setDbTransactions] = useState<any[]>([])
    const [selectedTx, setSelectedTx] = useState<any | null>(null)
    const [realP2POrders, setRealP2POrders] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState(true)

    const CURRENCY_OPTIONS = ['All Coins', 'USDT', 'BTC', 'ETH', 'SOL', 'XRP', 'AVAX', 'DOGE', 'BNB', 'ADA', 'DOT', 'MATIC', 'LINK']
    const TYPE_OPTIONS = ['All Types', 'Long', 'Short']

    const fetchTrades = async () => {
        setIsLoading(true)
        try {
            const [open, closed, txs, p2p] = await Promise.all([
                getMyOpenTrades(),
                getMyClosedTrades(),
                getTransactions(),
                getMyOrders({ status: 'completed' }).catch(() => ({ orders: [] })),
            ])
            setOpenSpotTrades((open || []).filter((t: any) => t.type === 'spot' && (t.status === 'open' || t.status === 'pending')).map(mapTrade))
            setHistoryTrades((closed || []).map(mapTrade))
            setDbTransactions(txs || [])
            // Map real P2P orders to display format
            const orders = p2p?.orders ?? []
            setRealP2POrders(orders.map((o: any) => {
                const isBuyer = o.buyer?._id === user?._id || o.buyer === user?._id
                return {
                    _id: o._id,
                    displayType: isBuyer ? 'P2P Buy' : 'P2P Sell',
                    pair: `${o.crypto}/${o.fiat}`,
                    asset: o.crypto,
                    amount: o.cryptoAmount,
                    amountText: `${parseFloat(o.cryptoAmount?.toFixed(8)).toString()} ${o.crypto}`,
                    price: `${o.fiatAmount?.toLocaleString()} ${o.fiat}`,
                    counterparty: isBuyer ? (o.seller?.name ?? 'Seller') : (o.buyer?.name ?? 'Buyer'),
                    orderNumber: o.orderNumber,
                    dateTime: new Date(o.createdAt).toLocaleString(),
                    status: o.status,
                    createdAt: o.createdAt,
                    isBuyer,
                }
            }))
        } catch (err) {
            console.error('Error fetching trades & transactions:', err)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => { fetchTrades() }, [])

    // Spot tab: active holdings first, then closed spot trades
    // Futures tab: closed/liquidated futures only
    const filteredHistory = historyTrades.filter(t => {
        const matchesTab = mainTab === 'spot' ? t.tradeType === 'spot' : t.tradeType === 'futures'
        const matchesCurrency = currencyFilter === 'All Coins' || t.asset === currencyFilter
        const matchesType = typeFilter === 'All Types' || t.type === typeFilter
        return matchesTab && matchesCurrency && matchesType
    })
    const filteredOpenSpot = openSpotTrades.filter(t => {
        const matchesCurrency = currencyFilter === 'All Coins' || t.asset === currencyFilter
        const matchesType = typeFilter === 'All Types' || t.type === typeFilter
        return matchesCurrency && matchesType
    })
    const displayTrades = mainTab === 'spot' ? [...filteredOpenSpot, ...filteredHistory] : filteredHistory

    // Filter DB transactions for Internal Transfers tab
    const transfers = dbTransactions.filter(t => t.type === 'transfer').map(t => ({
        ...t,
        displayType: 'Internal Funding Transfer',
        sentFrom: 'My Funding Account',
        destination: t.walletAddress || 'Internal Bicoin Account',
        amountText: `${formatAmount(t.amount)} ${t.asset}`,
        dateTime: new Date(t.createdAt).toLocaleString(),
        isProfit: false
    }))

    // Filter DB transactions — split into Deposit and Withdrawal tabs
    const mapGateway = (t: any) => ({
        ...t,
        displayType: t.type === 'deposit' ? 'On-chain Deposit' : 'On-chain Withdrawal',
        sentFrom: t.type === 'deposit' ? 'External Blockchain Wallet' : 'My Bicoin Spot Wallet',
        destination: t.type === 'deposit' ? 'My Bicoin Wallet Address' : (t.walletAddress || 'External Wallet Address'),
        amountText: `${formatAmount(t.amount)} ${t.asset}`,
        dateTime: new Date(t.createdAt).toLocaleString(),
        isProfit: t.type === 'deposit'
    })
    const deposits = dbTransactions.filter(t => t.type === 'deposit').map(mapGateway)
    const withdrawals = dbTransactions.filter(t => t.type === 'withdrawal').map(mapGateway)

    return (
        <Layout activePage="futures" hideMobileNav={true}>
            {/* Styles for glassmorphism and enhancements */}
            <style>{`
                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes scale-up {
                    from { transform: scale(0.96); opacity: 0; }
                    to { transform: scale(1); opacity: 1; }
                }
                .glass-modal-overlay {
                    backdrop-filter: blur(12px) !important;
                }
                .glass-modal-content {
                    background: rgba(13, 13, 23, 0.8) !important;
                    backdrop-filter: blur(25px) !important;
                    border: 1px solid rgba(255, 255, 255, 0.08) !important;
                    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.7) !important;
                }
                .clickable-row {
                    cursor: pointer;
                    transition: background 0.15s ease;
                }
                .clickable-row:hover {
                    background: rgba(255, 255, 255, 0.04) !important;
                }
                .history-primary-tabs button {
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .history-primary-tabs button:hover {
                    color: #1B9B8C !important;
                }
            `}</style>

            {/* Mobile View - Trade History Screen */}
            <div className="mobile-only">
                <div className="history-mobile-layout">
                    {/* Header */}
                    <div className="history-top-bar">
                        <button className="history-back-btn" onClick={() => navigate('/dashboard/assets')}>
                            <ArrowLeft size={20} />
                        </button>
                        <span className="history-top-title">History Center</span>
                    </div>

                    {/* Primary History Mobile Tabs */}
                    <div className="history-primary-tabs" style={{ display: 'flex', gap: '12px', overflowX: 'auto', padding: '12px 16px 8px', borderBottom: '1px solid #1C1C2C', whiteSpace: 'nowrap', WebkitOverflowScrolling: 'touch' }}>
                        {[
                            { key: 'trade', label: 'Trade History' },
                            { key: 'p2p', label: 'P2P Orders' },
                            { key: 'transfer', label: 'Transfers' },
                            { key: 'deposit', label: 'Deposit' },
                            { key: 'withdrawal', label: 'Withdrawal' }
                        ].map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setActiveHistoryTab(t.key as any)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: activeHistoryTab === t.key ? '#1B9B8C' : '#71717A',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    paddingBottom: '8px',
                                    borderBottom: activeHistoryTab === t.key ? '2px solid #1B9B8C' : '2px solid transparent',
                                }}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {/* Render Sub Tabs (Spot/Futures) only if Trade History is active */}
                    {activeHistoryTab === 'trade' && (
                        <div className="history-main-tabs">
                            <button 
                                className={`history-main-tab ${mainTab === 'spot' ? 'active' : ''}`}
                                onClick={() => setMainTab('spot')}
                            >
                                Spot
                            </button>
                            <button 
                                className={`history-main-tab ${mainTab === 'futures' ? 'active' : ''}`}
                                onClick={() => setMainTab('futures')}
                            >
                                Futures
                            </button>
                        </div>
                    )}

                    {/* Filter Row - Render only for Trade History */}
                    {activeHistoryTab === 'trade' && (
                        <div className="history-filter-row">
                            <div className="history-filters-left">
                                <div style={{ position: 'relative' }}>
                                    <button className="history-filter-btn" onClick={() => { setShowCurrencyDropdown(prev => !prev); setShowTypeDropdown(false) }}>
                                        <span>{currencyFilter}</span>
                                        <ChevronDown size={14} style={{ transform: showCurrencyDropdown ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
                                    </button>
                                    {showCurrencyDropdown && (
                                        <div style={{ position: 'absolute', top: '100%', left: 0, background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 8, zIndex: 20, marginTop: 4, minWidth: 140, maxHeight: 220, overflowY: 'auto' }}>
                                            {CURRENCY_OPTIONS.map(opt => (
                                                <div key={opt} onClick={() => { setCurrencyFilter(opt); setShowCurrencyDropdown(false) }}
                                                    style={{ padding: '10px 16px', color: 'var(--ui-text-primary)', cursor: 'pointer', background: currencyFilter === opt ? '#1C1C2C' : 'transparent', fontSize: 14 }}
                                                    onMouseEnter={e => (e.currentTarget.style.background = '#1C1C2C')}
                                                    onMouseLeave={e => (e.currentTarget.style.background = currencyFilter === opt ? '#1C1C2C' : 'transparent')}>
                                                    {opt}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div style={{ position: 'relative' }}>
                                    <button className="history-filter-btn" onClick={() => { setShowTypeDropdown(prev => !prev); setShowCurrencyDropdown(false) }}>
                                        <span>{typeFilter}</span>
                                        <ChevronDown size={14} style={{ transform: showTypeDropdown ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
                                    </button>
                                    {showTypeDropdown && (
                                        <div style={{ position: 'absolute', top: '100%', left: 0, background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 8, zIndex: 20, marginTop: 4, minWidth: 130 }}>
                                            {TYPE_OPTIONS.map(opt => (
                                                <div key={opt} onClick={() => { setTypeFilter(opt); setShowTypeDropdown(false) }}
                                                    style={{ padding: '10px 16px', color: 'var(--ui-text-primary)', cursor: 'pointer', background: typeFilter === opt ? '#1C1C2C' : 'transparent', fontSize: 14 }}
                                                    onMouseEnter={e => (e.currentTarget.style.background = '#1C1C2C')}
                                                    onMouseLeave={e => (e.currentTarget.style.background = typeFilter === opt ? '#1C1C2C' : 'transparent')}>
                                                    {opt}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Content Lists */}
                    {activeHistoryTab === 'trade' && (
                        isLoading ? (
                            <HistoryMobileSkeleton />
                        ) : displayTrades.length === 0 ? (
                            <div className="history-empty-state">
                                <span className="history-empty-text">No trades found</span>
                            </div>
                        ) : (
                            <div className="history-trades-list">
                                {displayTrades.map((trade) => (
                                    trade.tradeType === 'futures' ? (
                                        <div key={trade.id} className="history-trade-card-futures clickable-row" onClick={() => setSelectedTx(trade)}>
                                            <div className="tcf-top-row">
                                                <div className="tcf-badges">
                                                    <span className="tcf-pair">{trade.pair}</span>
                                                    <span className={`tcf-badge tcf-badge-side ${trade.type === 'Long' ? 'buy-up' : 'buy-down'}`}>{trade.type === 'Long' ? 'Buy Up' : 'Buy Down'}</span>
                                                    <span className="tcf-badge tcf-badge-leverage">{trade.leverage}x</span>
                                                    <span className="tcf-badge tcf-badge-isolated">Isolated</span>
                                                </div>
                                                <span className={`tcf-pnl ${trade.isProfit ? 'profit' : 'loss'}`}>{trade.pnl.replace('$', '')}</span>
                                            </div>
                                            <div className="tcf-detail-grid">
                                                <div className="tcf-detail-col">
                                                    <div className="tcf-detail-item"><span className="tcf-detail-label">Stop Profit</span><span className="tcf-detail-value">{trade.stopProfitText}</span></div>
                                                    <div className="tcf-detail-item"><span className="tcf-detail-label">Stop Loss</span><span className="tcf-detail-value">{trade.stopLossText}</span></div>
                                                </div>
                                                <div className="tcf-detail-col">
                                                    <div className="tcf-detail-item"><span className="tcf-detail-label">Open</span><span className="tcf-detail-value">{trade.openPriceText}</span></div>
                                                    <div className="tcf-detail-item"><span className="tcf-detail-label">Close</span><span className="tcf-detail-value">{trade.closePriceText}</span></div>
                                                </div>
                                                <div className="tcf-detail-col tcf-detail-col-right">
                                                    <span className="tcf-margin">Margin {trade.marginText}</span>
                                                </div>
                                            </div>
                                            <div className="tcf-footer-row">
                                                <span className="tcf-timestamp">{trade.timestampText}</span>
                                                <span className="tcf-status-note">{trade.statusNote}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div key={trade.id} className="history-trade-card clickable-row" onClick={() => setSelectedTx(trade)}>
                                            <div className="trade-card-header">
                                                <div className="trade-pair-info">
                                                    <span className="trade-pair">{trade.pair}</span>
                                                    <span className="trade-type">{trade.type}</span>
                                                </div>
                                                <div className="trade-pnl-info">
                                                    <span className={`trade-pnl ${trade.isProfit ? 'profit' : 'loss'}`}>{trade.pnl}</span>
                                                    <span className={`trade-pnl-percent ${trade.isProfit ? 'profit' : 'loss'}`}>{trade.pnlPercent}</span>
                                                </div>
                                            </div>
                                            <div className="trade-entry-price">{trade.entryPrice}</div>
                                        </div>
                                    )
                                ))}
                            </div>
                        )
                    )}

                    {activeHistoryTab === 'p2p' && (
                        isLoading ? (
                            <HistoryMobileSkeleton />
                        ) : (
                        <div className="history-trades-list" style={{ padding: '16px' }}>
                            {realP2POrders.length === 0 && (
                                <div className="history-empty-state">
                                    <span className="history-empty-text">No completed P2P orders</span>
                                </div>
                            )}
                            {realP2POrders.map((tx) => (
                                <div key={tx._id} className="history-trade-card clickable-row" onClick={() => setSelectedTx(tx)} style={{ marginBottom: '12px' }}>
                                    <div className="trade-card-header">
                                        <div>
                                            <span className="trade-pair" style={{ display: 'block', fontSize: '15px' }}>{tx.pair}</span>
                                            <span className="trade-type" style={{ color: tx.isBuyer ? '#1B9B8C' : '#EF4444' }}>{tx.displayType}</span>
                                            <span style={{ fontSize: 11, color: 'var(--ui-text-muted)', display: 'block' }}>with {tx.counterparty}</span>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <span style={{ display: 'block', fontWeight: 700, color: tx.isBuyer ? '#1B9B8C' : '#EF4444' }}>
                                                {tx.isBuyer ? '+' : '-'}{tx.amountText}
                                            </span>
                                            <span className="trade-pnl-percent" style={{ color: 'var(--ui-text-muted)' }}>{tx.price}</span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: 'var(--ui-text-muted)' }}>
                                        <span>{tx.dateTime}</span>
                                        <span style={{ color: '#1B9B8C', textTransform: 'uppercase', fontWeight: 600 }}>{tx.status}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        )
                    )}

                    {activeHistoryTab === 'transfer' && (
                        isLoading ? (
                            <HistoryMobileSkeleton />
                        ) : transfers.length === 0 ? (
                            <div className="history-empty-state">
                                <span className="history-empty-text">No transfers found</span>
                            </div>
                        ) : (
                            <div className="history-trades-list" style={{ padding: '16px' }}>
                                {transfers.map((tx) => (
                                    <div key={tx._id} className="history-trade-card clickable-row" onClick={() => setSelectedTx(tx)} style={{ marginBottom: '12px' }}>
                                        <div className="trade-card-header">
                                            <div>
                                                <span className="trade-pair" style={{ display: 'block', fontSize: '15px' }}>{tx.asset}</span>
                                                <span className="trade-type" style={{ color: '#1B9B8C' }}>{tx.displayType}</span>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <span className="trade-pnl loss" style={{ display: 'block', color: '#EF4444' }}>-{tx.amountText}</span>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: 'var(--ui-text-muted)' }}>
                                            <span>{tx.dateTime}</span>
                                            <span style={{ color: '#1B9B8C', textTransform: 'uppercase', fontWeight: 600 }}>{tx.status}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}

                    {activeHistoryTab === 'deposit' && (
                        isLoading ? (
                            <HistoryMobileSkeleton />
                        ) : deposits.length === 0 ? (
                            <div className="history-empty-state">
                                <span className="history-empty-text">No deposits found</span>
                            </div>
                        ) : (
                            <div className="history-trades-list" style={{ padding: '16px' }}>
                                {deposits.map((tx) => (
                                    <div key={tx._id} className="history-trade-card clickable-row" onClick={() => setSelectedTx(tx)} style={{ marginBottom: '12px' }}>
                                        <div className="trade-card-header">
                                            <div>
                                                <span className="trade-pair" style={{ display: 'block', fontSize: '15px' }}>{tx.asset}</span>
                                                <span className="trade-type" style={{ color: '#1B9B8C' }}>{tx.displayType}</span>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <span className="trade-pnl" style={{ display: 'block', color: '#1B9B8C' }}>+{tx.amountText}</span>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: 'var(--ui-text-muted)' }}>
                                            <span>{tx.dateTime}</span>
                                            <span style={{ color: '#1B9B8C', textTransform: 'uppercase', fontWeight: 600 }}>{tx.status}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}

                    {activeHistoryTab === 'withdrawal' && (
                        isLoading ? (
                            <HistoryMobileSkeleton />
                        ) : withdrawals.length === 0 ? (
                            <div className="history-empty-state">
                                <span className="history-empty-text">No withdrawals found</span>
                            </div>
                        ) : (
                            <div className="history-trades-list" style={{ padding: '16px' }}>
                                {withdrawals.map((tx) => (
                                    <div key={tx._id} className="history-trade-card clickable-row" onClick={() => setSelectedTx(tx)} style={{ marginBottom: '12px' }}>
                                        <div className="trade-card-header">
                                            <div>
                                                <span className="trade-pair" style={{ display: 'block', fontSize: '15px' }}>{tx.asset}</span>
                                                <span className="trade-type" style={{ color: '#EF4444' }}>{tx.displayType}</span>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <span className="trade-pnl" style={{ display: 'block', color: '#EF4444' }}>-{tx.amountText}</span>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: 'var(--ui-text-muted)' }}>
                                            <span>{tx.dateTime}</span>
                                            <span style={{ color: '#EF4444', textTransform: 'uppercase', fontWeight: 600 }}>{tx.status}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </div>
            </div>

            {/* Desktop View */}
            <div className="desktop-only">
                <div className="history-desktop-container" style={{ padding: '60px 40px', maxWidth: '1200px', margin: '0 auto' }}>
                    <h1 className="history-desktop-title" style={{ fontSize: '32px', fontWeight: 700, color: 'var(--ui-text-primary)', marginBottom: '8px' }}>History Center</h1>
                    <p style={{ color: 'var(--ui-text-muted)', fontSize: '14px', marginBottom: '32px' }}>Review trades, P2P transactions, internal ledger transfers, and gateway movements</p>
                    
                    {/* Primary History Desktop Tabs */}
                    <div className="history-primary-tabs" style={{ display: 'flex', gap: '28px', marginBottom: '28px', borderBottom: '1px solid #1C1C2C', paddingBottom: '2px' }}>
                        {[
                            { key: 'trade', label: 'Trade History' },
                            { key: 'p2p', label: 'P2P Transactions' },
                            { key: 'transfer', label: 'Internal Ledger Transfers' },
                            { key: 'deposit', label: 'Deposit' },
                            { key: 'withdrawal', label: 'Withdrawal' }
                        ].map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setActiveHistoryTab(t.key as any)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: activeHistoryTab === t.key ? '#1B9B8C' : '#71717A',
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    paddingBottom: '14px',
                                    borderBottom: activeHistoryTab === t.key ? '2.5px solid #1B9B8C' : '2.5px solid transparent',
                                }}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {/* Trade History Sub Tabs */}
                    {activeHistoryTab === 'trade' && (
                        <div className="history-desktop-tabs" style={{ marginBottom: '24px' }}>
                            <button 
                                className={`history-desktop-tab ${mainTab === 'spot' ? 'active' : ''}`}
                                onClick={() => setMainTab('spot')}
                            >
                                Spot
                            </button>
                            <button 
                                className={`history-desktop-tab ${mainTab === 'futures' ? 'active' : ''}`}
                                onClick={() => setMainTab('futures')}
                            >
                                Futures
                            </button>
                        </div>
                    )}

                    {/* Conditionally Render Table Based on Active Tab */}
                    {activeHistoryTab === 'trade' && (
                        <div className="history-desktop-table" style={{ background: 'var(--ui-bg-primary)', border: '1px solid var(--ui-border)', borderRadius: '16px', padding: '16px', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Pair</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Type</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Entry Price</th>
                                        {mainTab === 'futures' && (
                                            <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Close Price</th>
                                        )}
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>PnL</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>PnL %</th>
                                        {mainTab === 'futures' && (
                                            <>
                                                <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Leverage</th>
                                                <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Margin</th>
                                                <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Status</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <HistoryDesktopSkeleton cols={mainTab === 'futures' ? 8 : 5} />
                                    ) : displayTrades.length === 0 ? (
                                        <tr>
                                            <td colSpan={mainTab === 'futures' ? 8 : 5} style={{ textAlign: 'center', padding: '32px', color: 'var(--ui-text-muted)' }}>No trade records found</td>
                                        </tr>
                                    ) : (
                                        displayTrades.map((trade) => (
                                            <tr key={trade.id} className="clickable-row" onClick={() => setSelectedTx(trade)}>
                                                <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{trade.pair}</td>
                                                <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{trade.type === 'Long' ? 'Buy Up' : 'Buy Down'}</td>
                                                <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{trade.entryPrice}</td>
                                                {mainTab === 'futures' && (
                                                    <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{trade.closePriceText}</td>
                                                )}
                                                <td style={{ padding: '16px', fontSize: '14px', fontWeight: 700, borderBottom: '1px solid #1C1C2C' }} className={trade.isProfit ? 'profit' : 'loss'}>{trade.pnl}</td>
                                                <td style={{ padding: '16px', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }} className={trade.isProfit ? 'profit' : 'loss'}>{trade.pnlPercent}</td>
                                                {mainTab === 'futures' && (
                                                    <>
                                                        <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{trade.leverage}x Isolated</td>
                                                        <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{trade.marginText}</td>
                                                        <td style={{ padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', borderBottom: '1px solid #1C1C2C' }}>{trade.statusNote}</td>
                                                    </>
                                                )}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {activeHistoryTab === 'p2p' && (
                        <div className="history-desktop-table" style={{ background: 'var(--ui-bg-primary)', border: '1px solid var(--ui-border)', borderRadius: '16px', padding: '16px', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Pair</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Type</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Quantity</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Price</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Date</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <HistoryDesktopSkeleton cols={6} />
                                    ) : realP2POrders.length === 0 ? (
                                        <tr><td colSpan={6} style={{ textAlign: 'center', padding: '32px', color: 'var(--ui-text-muted)' }}>No completed P2P orders found</td></tr>
                                    ) : (
                                    realP2POrders.map((tx) => (
                                        <tr key={tx._id} className="clickable-row" onClick={() => setSelectedTx(tx)}>
                                            <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{tx.pair}</td>
                                            <td style={{ padding: '16px', color: tx.displayType.includes('Buy') ? '#1B9B8C' : '#EF4444', fontSize: '14px', fontWeight: 600, borderBottom: '1px solid #1C1C2C' }}>{tx.displayType}</td>
                                            <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{tx.amountText}</td>
                                            <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{tx.price}</td>
                                            <td style={{ padding: '16px', color: 'var(--ui-text-muted)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{tx.dateTime}</td>
                                            <td style={{ padding: '16px', borderBottom: '1px solid #1C1C2C' }}><span className="status-badge completed">Completed</span></td>
                                        </tr>
                                    ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {activeHistoryTab === 'transfer' && (
                        <div className="history-desktop-table" style={{ background: 'var(--ui-bg-primary)', border: '1px solid var(--ui-border)', borderRadius: '16px', padding: '16px', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Asset</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Type</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Amount</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Recipient / Address</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Date</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <HistoryDesktopSkeleton cols={6} />
                                    ) : transfers.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} style={{ textAlign: 'center', padding: '32px', color: 'var(--ui-text-muted)' }}>No internal transfer records found</td>
                                        </tr>
                                    ) : (
                                        transfers.map((tx) => (
                                            <tr key={tx._id} className="clickable-row" onClick={() => setSelectedTx(tx)}>
                                                <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{tx.asset}</td>
                                                <td style={{ padding: '16px', color: '#1B9B8C', fontSize: '14px', fontWeight: 600, borderBottom: '1px solid #1C1C2C' }}>{tx.displayType}</td>
                                                <td style={{ padding: '16px', color: '#EF4444', fontSize: '14px', fontWeight: 600, borderBottom: '1px solid #1C1C2C' }}>-{tx.amountText}</td>
                                                <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{tx.destination}</td>
                                                <td style={{ padding: '16px', color: 'var(--ui-text-muted)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{tx.dateTime}</td>
                                                <td style={{ padding: '16px', borderBottom: '1px solid #1C1C2C' }}><span className={`status-badge ${tx.status}`}>{tx.status}</span></td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {activeHistoryTab === 'deposit' && (
                        <div className="history-desktop-table" style={{ background: 'var(--ui-bg-primary)', border: '1px solid var(--ui-border)', borderRadius: '16px', padding: '16px', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Asset</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Amount</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>From</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Date</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <HistoryDesktopSkeleton cols={5} />
                                    ) : deposits.length === 0 ? (
                                        <tr><td colSpan={5} style={{ textAlign: 'center', padding: '32px', color: 'var(--ui-text-muted)' }}>No deposit records found</td></tr>
                                    ) : (
                                        deposits.map((tx) => (
                                            <tr key={tx._id} className="clickable-row" onClick={() => setSelectedTx(tx)}>
                                                <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{tx.asset}</td>
                                                <td style={{ padding: '16px', color: '#1B9B8C', fontSize: '14px', fontWeight: 600, borderBottom: '1px solid #1C1C2C' }}>+{tx.amountText}</td>
                                                <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{tx.sentFrom}</td>
                                                <td style={{ padding: '16px', color: 'var(--ui-text-muted)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{tx.dateTime}</td>
                                                <td style={{ padding: '16px', borderBottom: '1px solid #1C1C2C' }}><span className={`status-badge ${tx.status}`}>{tx.status}</span></td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {activeHistoryTab === 'withdrawal' && (
                        <div className="history-desktop-table" style={{ background: 'var(--ui-bg-primary)', border: '1px solid var(--ui-border)', borderRadius: '16px', padding: '16px', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Asset</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Amount</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Destination</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Date</th>
                                        <th style={{ textAlign: 'left', padding: '16px', color: 'var(--ui-text-muted)', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid #1C1C2C' }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <HistoryDesktopSkeleton cols={5} />
                                    ) : withdrawals.length === 0 ? (
                                        <tr><td colSpan={5} style={{ textAlign: 'center', padding: '32px', color: 'var(--ui-text-muted)' }}>No withdrawal records found</td></tr>
                                    ) : (
                                        withdrawals.map((tx) => (
                                            <tr key={tx._id} className="clickable-row" onClick={() => setSelectedTx(tx)}>
                                                <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{tx.asset}</td>
                                                <td style={{ padding: '16px', color: '#EF4444', fontSize: '14px', fontWeight: 600, borderBottom: '1px solid #1C1C2C' }}>-{tx.amountText}</td>
                                                <td style={{ padding: '16px', color: 'var(--ui-text-primary)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }} title={tx.destination}>
                                                    {tx.destination.length > 30 ? `${tx.destination.substring(0, 20)}...${tx.destination.substring(tx.destination.length - 8)}` : tx.destination}
                                                </td>
                                                <td style={{ padding: '16px', color: 'var(--ui-text-muted)', fontSize: '14px', borderBottom: '1px solid #1C1C2C' }}>{tx.dateTime}</td>
                                                <td style={{ padding: '16px', borderBottom: '1px solid #1C1C2C' }}><span className={`status-badge ${tx.status}`}>{tx.status}</span></td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Deposit / Withdrawal Full-Screen Detail View ── */}
            {selectedTx && (selectedTx.type === 'deposit' || selectedTx.type === 'withdrawal') && (
                <TxDetailScreen tx={selectedTx} onBack={() => setSelectedTx(null)} />
            )}

            {/* ── Generic bottom sheet for trade / p2p / transfer ── */}
            {selectedTx && selectedTx.type !== 'deposit' && selectedTx.type !== 'withdrawal' && (
                <div onClick={() => setSelectedTx(null)} style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.6)', zIndex: 9999,
                    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                }}>
                    <div onClick={(e) => e.stopPropagation()} style={{
                        background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderBottom: 'none',
                        borderRadius: '20px 20px 0 0', padding: '20px 20px 28px', width: '100%', maxWidth: '480px',
                        color: 'var(--ui-text-primary)', boxShadow: '0 -8px 40px rgba(0,0,0,0.4)', position: 'relative',
                        maxHeight: '85vh', overflowY: 'auto',
                    }}>
                        <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--ui-border)', margin: '0 auto 16px' }} />
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid var(--ui-border)' }}>
                            <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Transaction Details</h3>
                            <button onClick={() => setSelectedTx(null)} style={{ background: 'none', border: 'none', color: 'var(--ui-text-muted)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {[
                                { label: 'Type', value: selectedTx.displayType || selectedTx.type },
                                ...(selectedTx.tradeType === 'futures' ? [
                                    { label: 'Leverage', value: `${selectedTx.leverage}x Isolated` },
                                    { label: 'Open Price', value: `$${selectedTx.openPriceText}` },
                                    { label: 'Close Price', value: selectedTx.closePriceText !== '—' ? `$${selectedTx.closePriceText}` : '—' },
                                    { label: 'Stop Profit', value: selectedTx.stopProfitText !== '—' ? `$${selectedTx.stopProfitText}` : '—' },
                                    { label: 'Stop Loss', value: selectedTx.stopLossText !== '—' ? `$${selectedTx.stopLossText}` : '—' },
                                    { label: 'Margin', value: `$${selectedTx.marginText}` },
                                ] : []),
                                { label: 'Amount', value: `${selectedTx.isProfit ? '+' : '-'}${selectedTx.amountText || selectedTx.pnl}`, color: selectedTx.isProfit ? '#1CD4A7' : '#EF4444' },
                                { label: 'Date', value: selectedTx.timestampText || selectedTx.dateTime || new Date(selectedTx.createdAt).toLocaleString() },
                                { label: 'Status', value: selectedTx.statusNote || selectedTx.status || 'completed' },
                            ].map(row => (
                                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: 'var(--ui-text-muted)', fontSize: 13 }}>{row.label}</span>
                                    <span style={{ fontWeight: 600, fontSize: 14, color: row.color || 'var(--ui-text-primary)' }}>{row.value}</span>
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setSelectedTx(null)} style={{ width: '100%', background: '#1B9B8C', color: '#fff', border: 'none', borderRadius: 12, padding: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 24 }}>Dismiss</button>
                    </div>
                </div>
            )}
        </Layout>
    )
}

// ─────────────────────────────────────────────────────────────
// Deposit / Withdrawal Detail Full-Screen Component
// ─────────────────────────────────────────────────────────────
export function TxDetailScreen({ tx: initialTx, onBack }: { tx: any; onBack: () => void }) {
    const [copiedField, setCopiedField] = useState<string | null>(null)
    const [tx, setTx] = useState(initialTx)
    const [checking, setChecking] = useState(false)
    // Anything reaching this screen that isn't an outgoing on-chain withdrawal is
    // rendered as a "deposit"-style view — this also covers 'adjustment' (manual
    // admin credits) shown from the Deposit page, which previously fell through
    // to the withdrawal title because it isn't literally type 'deposit'.
    const isDeposit = tx.type !== 'withdrawal'
    const isCompleted = tx.status === 'completed'
    const isPending = tx.status === 'pending'

    // Plisio doesn't send a webhook when a withdrawal completes on-chain (only
    // deposits get that) — so re-check live status when this screen opens, and
    // keep polling every 15s while it's still "processing".
    useEffect(() => {
        if (isDeposit || tx.status !== 'processing' || !tx._id) return
        let cancelled = false
        const check = async () => {
            setChecking(true)
            try {
                const updated = await checkWithdrawalStatus(tx._id)
                if (!cancelled && updated) setTx(updated)
            } catch { /* ignore — will retry on next interval */ }
            finally { if (!cancelled) setChecking(false) }
        }
        check()
        const interval = setInterval(check, 15000)
        return () => { cancelled = true; clearInterval(interval) }
    }, [tx._id, tx.status, isDeposit])

    const copy = (text: string, field: string) => {
        if (!text || text === 'N/A') return
        navigator.clipboard.writeText(text)
        setCopiedField(field)
        setTimeout(() => setCopiedField(null), 2000)
    }

    const timeStr = tx.createdAt
        ? new Date(tx.createdAt).toLocaleString('sv').replace('T', ' ')
        : tx.dateTime || '—'

    // Fee was historically stored as a PERCENTAGE (e.g. 2.5 = 2.5%), and older
    // withdrawal records don't have feeAmount/netAmount populated at all — fall
    // back to deriving them so this never shows the raw percentage as if it were
    // a currency amount (the bug: "10 USDT" fee on a 10 USDT withdrawal, when the
    // "10" was actually a 10% rate).
    const grossAmount = Number(tx.amount) || 0
    const feeAmount = tx.feeAmount > 0
        ? Number(tx.feeAmount)
        : (tx.fee ? grossAmount * (Number(tx.fee) / 100) : 0)
    const netAmount = tx.netAmount > 0
        ? Number(tx.netAmount)
        : (grossAmount - feeAmount)
    const fmtAmt = (n: number) => formatAmount(n)

    // Withdrawal status steps
    const withdrawalSteps = [
        { key: 'submitted', label: 'Submitted', done: true },
        { key: 'processing', label: 'Processing', done: tx.status === 'processing' || tx.status === 'completed' },
        { key: 'completed', label: 'Completed', done: tx.status === 'completed', time: isCompleted ? timeStr : undefined },
    ]

    const statusColor = isCompleted ? '#1CD4A7' : isPending ? '#F59E0B' : tx.status === 'processing' ? '#3B82F6' : '#EF4444'

    const detailRows: { label: string; value: string; copyKey?: string; highlight?: boolean }[] = [
        { label: 'Transaction ID', value: tx._id || '—', copyKey: 'transactionId' },
        { label: 'Type', value: tx.type === 'adjustment' ? 'Balance Adjustment' : (isDeposit ? 'On-Chain Deposit' : 'On-Chain Withdrawal') },
        ...(tx.type !== 'adjustment' ? [
            { label: 'Network', value: tx.network || 'TRC20' },
            // Older withdrawals created before the Plisio auto-withdrawal integration
            // never had an on-chain address/txid recorded — say so instead of a bare "-".
            { label: 'Address', value: tx.walletAddress || 'Not recorded for this transaction', copyKey: tx.walletAddress ? 'address' : undefined },
            { label: 'Txid', value: tx.txHash || (isPending || tx.status === 'processing' ? 'Pending on-chain confirmation' : 'Not recorded for this transaction'), copyKey: tx.txHash ? 'txid' : undefined },
        ] : []),
        ...(!isDeposit && feeAmount > 0 ? [
            { label: 'Amount Sent', value: `${fmtAmt(grossAmount)} ${tx.asset}` },
            { label: 'Network Fee (flat)', value: `${fmtAmt(feeAmount)} ${tx.asset}` },
            { label: 'Recipient Receives', value: `${fmtAmt(netAmount)} ${tx.asset}`, highlight: true },
        ] : []),
        { label: 'Time', value: timeStr },
    ]

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'var(--ui-bg-primary)', zIndex: 9999, overflowY: 'auto',
            display: 'flex', flexDirection: 'column',
        }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 16px 12px', borderBottom: '1px solid #1C1C2C' }}>
                <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--ui-text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 10, margin: -10 }}>
                    <ArrowLeft size={22} />
                </button>
                <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--ui-text-primary)' }}>
                    {isDeposit ? 'Deposit Details' : 'Withdrawal Details'}
                </span>
                {!isDeposit && tx.status === 'processing' && (
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ui-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        {checking ? 'Checking…' : 'Auto-refreshing'}
                    </span>
                )}
            </div>

            <div style={{ padding: '28px 16px 40px', display: 'flex', flexDirection: 'column', gap: 24 }}>

                {/* ── Hero ── */}
                {isDeposit ? (
                    /* Deposit hero: circle check + status + amount */
                    <div style={{ textAlign: 'center', paddingBottom: 8 }}>
                        <div style={{ width: 68, height: 68, borderRadius: '50%', background: 'rgba(28,212,167,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                            <div style={{ width: 50, height: 50, borderRadius: '50%', background: '#1CD4A7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: statusColor, marginBottom: 6, textTransform: 'capitalize' }}>{tx.status}</div>
                        <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--ui-text-primary)', fontVariantNumeric: 'tabular-nums' }}>{formatAmount(tx.amount)} {tx.asset}</div>
                        <div style={{ fontSize: 13, color: 'var(--ui-text-muted)', marginTop: 8 }}>
                            Crypto has arrived in your Bicoin account. View your spot account balance for more details.
                        </div>
                    </div>
                ) : (
                    /* Withdrawal hero: amount + step timeline */
                    <div style={{ paddingBottom: 8 }}>
                        <div style={{ textAlign: 'center', marginBottom: 24 }}>
                            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--ui-text-primary)', fontVariantNumeric: 'tabular-nums' }}>{formatAmount(tx.amount)} {tx.asset}</div>
                            <div style={{ fontSize: 13, color: 'var(--ui-text-muted)', marginTop: 4 }}>Amount</div>
                        </div>
                        {/* Step timeline */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, paddingLeft: 8 }}>
                            {withdrawalSteps.map((step, i) => (
                                <div key={step.key} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <div style={{
                                            width: 24, height: 24, borderRadius: '50%',
                                            background: step.done ? '#1CD4A7' : '#1C1C2C',
                                            border: step.done ? 'none' : '2px solid #2a2a3a',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                        }}>
                                            {step.done && <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#000" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                        </div>
                                        {i < withdrawalSteps.length - 1 && (
                                            <div style={{ width: 2, height: 28, background: step.done ? '#1CD4A7' : '#1C1C2C', margin: '2px 0' }} />
                                        )}
                                    </div>
                                    <div style={{ paddingBottom: i < withdrawalSteps.length - 1 ? 0 : 0, marginTop: 2 }}>
                                        <div style={{ fontSize: 14, fontWeight: 600, color: step.done ? '#fff' : '#52525B' }}>{step.label}</div>
                                        {step.time && <div style={{ fontSize: 12, color: 'var(--ui-text-muted)', marginTop: 2 }}>{step.time}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {isCompleted && (
                            <div style={{ marginTop: 16, fontSize: 13, color: 'var(--ui-text-muted)', textAlign: 'center', lineHeight: 1.6 }}>
                                Crypto transferred out of Bicoin. Please contact the recipient platform for your transaction receipt.
                            </div>
                        )}
                    </div>
                )}

                {/* ── Detail rows card ── */}
                <div style={{ background: 'var(--ui-bg-card)', borderRadius: 12, border: '1px solid var(--ui-border)', overflow: 'hidden' }}>
                    {detailRows.map((row, i) => (
                        <div key={row.label} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                            padding: '13px 16px',
                            borderBottom: i < detailRows.length - 1 ? '1px solid #1C1C2C' : 'none',
                            background: row.highlight ? 'rgba(28,212,167,0.06)' : 'transparent',
                        }}>
                            <span style={{ fontSize: 13, color: row.highlight ? 'var(--ui-text-primary)' : 'var(--ui-text-muted)', fontWeight: row.highlight ? 600 : 400, flexShrink: 0, marginRight: 12 }}>{row.label}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', flex: 1 }}>
                                <span style={{
                                    fontSize: row.highlight ? 14 : 13,
                                    color: row.highlight ? '#1CD4A7' : 'var(--ui-text-primary)',
                                    fontWeight: row.highlight ? 700 : 500,
                                    wordBreak: 'break-all', textAlign: 'right',
                                    fontFamily: row.copyKey ? 'monospace' : 'inherit',
                                }}>
                                    {row.value}
                                </span>
                                {row.copyKey && row.value && row.value !== '—' && (
                                    <button
                                        onClick={() => copy(row.value, row.copyKey!)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedField === row.copyKey ? '#1CD4A7' : '#52525B', flexShrink: 0, padding: 2, display: 'flex' }}
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
